import { useLocation } from '@remix-run/react';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { ProductSizing } from '~/common/constants/dictionary';
import { SIZE_ABBREVIATIONS_MAP } from '~/common/constants/product';
import { isBrowser } from '~/common/utils/browser';
import {
  CartActions,
  getStockLimit,
  isNumberAlreadyInCart,
} from '~/common/utils/cart';
import {
  getBottomSizes,
  getProductForVariantId,
  getSizes,
  getVariantId,
  isAvailable,
  isSetProduct,
  isVariantCombinationAvailable,
} from '~/common/utils/product';
import ColorItems from '~/components/bubbleItems/ColorItems';
import SizeItems from '~/components/bubbleItems/SizeItems';
import { Box } from '~/components/wrappers/Box';
import { Flex } from '~/components/wrappers/Flex';
import { Image } from '~/components/wrappers/Image';
import AddToBagUnderlineIcon from 'public/assets/icons/add-to-bag-underline.svg';
import { useCartSSR } from '~/recoil/useCartSSR';
import useColor from '~/recoil/useColor';
import { colorMid } from '~/theme/colors';
import { fontFamilies } from '~/theme/typography';
import { MAX_CART_AMOUNT_IN_DROPS } from '~/types/cart';
import { CollectionByHandle } from '~/types/dto/collectionsDto';
import { Product, ProductType, Variant } from '~/types/product';
import { evaluateFreeItemRule, FREE_ITEM_MESSAGES } from '~/lib/freeItemRule';
import { trackAddToCart } from '~/common/utils/addToCartTracking';

export interface ProductSettings {
  selectedColor: string;
  selectedSize: string;
  selectedBottomSize: string;
  selectedImage: string | null;
}

interface SelectionControlProps {
  product: Product;
  variants: Array<Variant>;
  productSettings: ProductSettings;
  setProductSettings: React.Dispatch<React.SetStateAction<ProductSettings>>;
  toggleSizingModal: () => void;
  productSizing?: ProductSizing;
  collectionsByHandle?: CollectionByHandle[];
  productsByHandle?: Array<Product>;
}

export const SelectionControls: FC<SelectionControlProps> = ({
  product,
  variants,
  productSettings,
  setProductSettings,
  toggleSizingModal,
  productSizing,
  collectionsByHandle,
  productsByHandle,
}) => {
  const [cartState, setCartState] = useCartSSR();
  const { pathname } = useLocation();
  const { title } = product;
  const { selectedColor, selectedSize, selectedBottomSize } = productSettings;
  const isSet = isSetProduct(product);
  const sizes = getSizes(selectedColor, variants) || [];
  const bottomSizes = isSet
    ? getBottomSizes(selectedColor, selectedSize, variants)
    : [];

  const isMysteryDrop =
    isBrowser && window?.ENV?.PUBLIC_MYSTERY_COLLECTION_NAME !== '';

  const availableMysteryVariants = useMemo(() => {
    const descriptors: Array<{
      productId: string;
      size: string;
      bottomSize: string;
    }> = [];

    if (!collectionsByHandle?.length) return descriptors;

    const collectionItem = collectionsByHandle.find(
      (item) => item.collectionByHandle.title === product?.title,
    );

    const collectionProducts =
      collectionItem?.collectionByHandle.products.edges;
    if (!collectionProducts) return descriptors;

    for (const item of collectionProducts) {
      for (const variant of item.node.variants.edges) {
        if (!variant.node.availableForSale) continue;
        const size =
          variant.node.selectedOptions.find((o) => o.name === 'Size')?.value ??
          '';
        const bottomSize =
          variant.node.selectedOptions.find((o) => o.name === 'Bottom Size')
            ?.value ?? '';
        descriptors.push({ productId: product.id, size, bottomSize });
      }
    }

    return descriptors;
  }, [collectionsByHandle, product?.id, product?.title]);

  const getAvailableVariantOptions = (id: string) => {
    if (availableMysteryVariants.length === 0) return 0;

    const matches = availableMysteryVariants.filter((d) => {
      if (d.productId !== id) return false;
      if (selectedSize && d.size !== selectedSize) return false;
      if (selectedBottomSize && d.bottomSize !== selectedBottomSize)
        return false;

      return true;
    });

    return Math.min(matches.length, MAX_CART_AMOUNT_IN_DROPS);
  };

  const isMaxNumberAlreadyInCart = (title: string, id: string) => {
    if (!isMysteryDrop) {
      return false;
    }

    const _availableVariantOptions = getAvailableVariantOptions(id);

    return isNumberAlreadyInCart(
      _availableVariantOptions,
      title,
      cartState.items?.lineItems,
      selectedSize,
      isSet ? selectedBottomSize : undefined,
    );
  };

  const isFreeItem = Number(product.prices.maxPrice) === 0;

  /**
   * The shared rule, evaluated against the cart AS IT WOULD BE after this add — the same
   * function the checkout action runs (`app/lib/freeItemRule.ts`).
   *
   * This replaces a per-PRODUCT cap (`freeItemQuantity < 1`, matched on
   * `item.handle.includes(product.handle)`), which let two DIFFERENT free products both
   * through. The ticket's acceptance criteria name that case explicitly: "never two or more
   * free items, including when different free products are used".
   *
   * The pushed line uses the handle as its id because no variant is resolved until a size
   * is chosen; the rule only reads `id` for traceability.
   *
   * Derived, not stored, as the flash-sale layout does it. It used to be mirrored into a
   * `freeItemBlock` state by one effect and reset to `'idle'` by a `[product.id]` effect —
   * which ran second on mount and won, so the button was disabled by the rule below while
   * the message never rendered.
   */
  const prospectiveViolation = useMemo(() => {
    if (!isFreeItem) return null;

    const result = evaluateFreeItemRule([
      ...cartState.items.lineItems.map((item) => ({
        id: item.variantId,
        price: item.price,
        quantity: item.quantity,
      })),
      { id: product.handle, price: 0, quantity: 1 },
    ]);

    return result.ok ? null : result.violation;
  }, [isFreeItem, cartState.items.lineItems, product.handle]);

  // Live stock for the selected variant in the shopper's market, against what the bag
  // already holds. Unlimited (untracked/backorder) variants have no limit.
  const selectedVariantId = getVariantId(variants, {
    color: selectedColor,
    size: selectedSize,
    bottomSize: isSet ? selectedBottomSize : undefined,
  });
  const stockLimit = getStockLimit(
    variants.find((variant) => variant.id === selectedVariantId)
      ?.quantityAvailable,
  );
  const stockLimitReached =
    !!stockLimit &&
    (cartState.items.lineItems.find(
      (item) => item.variantId === selectedVariantId,
    )?.quantity ?? 0) >= stockLimit;

  const Disclaimer = () => {
    if (stockLimitReached) {
      return (
        <QuantityDisclaimer>
          No more available{selectedSize ? ' in this size' : ''}
        </QuantityDisclaimer>
      );
    }

    if (prospectiveViolation) {
      return (
        <QuantityDisclaimer>
          {FREE_ITEM_MESSAGES[prospectiveViolation]}
        </QuantityDisclaimer>
      );
    }

    if (!isMysteryDrop) {
      return null;
    }

    const _availableVariantOptions = getAvailableVariantOptions(product.id);

    return (
      <QuantityDisclaimer>
        sorry! you cannot order more than {_availableVariantOptions} of this
        item
      </QuantityDisclaimer>
    );
  };

  const _createAvailabilityRules = () => {
    return [
      {
        condition: () => !isMaxNumberAlreadyInCart(product.title, product.id),
      },
      {
        condition: () => !product.tags.includes('comingsoon'),
      },
      {
        condition: () => !stockLimitReached,
      },
      {
        condition: () => isAvailable(selectedColor, variants),
      },
      {
        // Was a per-product cap. The shared rule covers the whole cart, so it also catches
        // a second free item of a DIFFERENT product.
        condition: () => !isFreeItem || prospectiveViolation === null,
      },
    ];
  };

  const _requiresSelectionAvailable = _createAvailabilityRules().every((rule) =>
    rule.condition(),
  );

  const isSelectionComplete =
    (!sizes.length || !!selectedSize) && (!isSet || !!selectedBottomSize);

  // Sold out / limits win: the button stays "Add to bag" and disabled. A missing size keeps
  // it enabled, so the tap can take the shopper to the picker instead of doing nothing.
  const needsSize = _requiresSelectionAvailable && !isSelectionComplete;
  const addToBagDisabled = !_requiresSelectionAvailable;

  const sizeSectionRef = useRef<HTMLDivElement>(null);
  const [sizePromptShown, setSizePromptShown] = useState(false);

  const showDisclaimer = !_requiresSelectionAvailable;

  useEffect(() => {
    if (!selectedColor) {
      const _initialColor = variants
        .find(
          (item) =>
            item.availableForSale &&
            item.options.some((option) => option.name === 'Color'),
        )
        ?.options.find((option) => option.name === 'Color')?.value;
      setProductSettings({
        ...productSettings,
        selectedColor: _initialColor || '',
      });
    }
  }, []);

  const displayColorSelect = product?.productType !== ProductType.Sunglasses;

  const _sanitizedSizes = sizes.filter(
    (item) => item.size !== 'XXX-LARGE' && item.size !== 'XX-SMALL',
  );

  const isClothingSize =
    SIZE_ABBREVIATIONS_MAP[
      _sanitizedSizes[0]?.size as keyof typeof SIZE_ABBREVIATIONS_MAP
    ];

  const handleUpdateCart = () => {
    if (needsSize) {
      setSizePromptShown(true);
      // `nearest`: no-op on desktop, where the picker sits above the button; on mobile the
      // button is pinned to the viewport bottom and the picker can be off-screen.
      sizeSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
      return;
    }

    if (!_requiresSelectionAvailable) {
      return;
    }

    const id = selectedVariantId;
    // Use the product that owns this variant so pattern metafield matches the selected color
    const productForVariant = productsByHandle?.length
      ? getProductForVariantId(productsByHandle, id)
      : undefined;
    const productToAdd: Product = productForVariant ?? product;

    const newCartState = CartActions.addCartItem(
      cartState.items,
      {
        ...productToAdd,
        selectedSize,
        selectedColor,
        selectedBottomSize: isSet ? selectedBottomSize : undefined,
      },
      id,
      pathname,
    );

    // Same object back: the line is already at its stock limit.
    if (newCartState === cartState.items) return;

    setCartState({
      items: newCartState,
    });

    trackAddToCart({
      product: productToAdd,
      variantId: id,
      cart: newCartState,
      pathname,
      size: selectedSize,
      bottomSize: isSet ? selectedBottomSize : undefined,
    });
  };

  const { color } = useColor();

  if (!product) return null;

  const colorName = selectedColor.split(' - ')[1] || '';

  return (
    <SelectionControlsContainer>
      {displayColorSelect && (
        <Section className="flash-sale-color">
          <ColorLabel>Color{colorName ? `: ${colorName}` : ''}</ColorLabel>
          <ColorItems
            variants={variants}
            selectedColor={selectedColor}
            selectedSize={selectedSize}
            setProductSettings={setProductSettings}
            productsByHandle={productsByHandle}
            product={product}
          />
        </Section>
      )}
      {isClothingSize ? (
        <Section className="flash-sale-size" ref={sizeSectionRef}>
          <SizeSectionContent>
            <SizeLabelsWrapper className="flash-sale-size-header">
              <SizeLabel>{isSet ? 'Top size' : 'Size'}</SizeLabel>
              {isClothingSize && productSizing?.title ? (
                <SizeGuideButton onClick={toggleSizingModal}>
                  Size guide
                </SizeGuideButton>
              ) : null}
            </SizeLabelsWrapper>
            <SizeItems
              sizes={sizes}
              setProductSettings={isSet ? undefined : setProductSettings}
              selectedSize={selectedSize}
              onSelect={
                isSet
                  ? (size) =>
                      setProductSettings((prev) => ({
                        ...prev,
                        selectedSize: size,
                        selectedBottomSize:
                          prev.selectedBottomSize &&
                          isVariantCombinationAvailable(
                            variants,
                            selectedColor,
                            size,
                            prev.selectedBottomSize,
                          )
                            ? prev.selectedBottomSize
                            : '',
                      }))
                  : undefined
              }
              title={title}
              showStars
              strokeColor={colorMid}
            />
          </SizeSectionContent>
        </Section>
      ) : null}
      {isSet && bottomSizes.length > 0 ? (
        <Section className="flash-sale-size">
          <SizeSectionContent>
            <SizeLabelsWrapper className="flash-sale-size-header">
              <SizeLabel>Bottom size</SizeLabel>
            </SizeLabelsWrapper>
            <SizeItems
              sizes={bottomSizes}
              selectedSize={selectedBottomSize}
              onSelect={(size) =>
                setProductSettings((prev) => ({
                  ...prev,
                  selectedBottomSize: size,
                }))
              }
              title={`${title} bottom`}
              showStars
              strokeColor={colorMid}
            />
          </SizeSectionContent>
        </Section>
      ) : null}
      {sizePromptShown && needsSize ? (
        <QuantityDisclaimer role="alert" $align="left">
          Please select a size
        </QuantityDisclaimer>
      ) : null}
      <AddToBagButton disabled={addToBagDisabled} onClick={handleUpdateCart}>
        <AddToBagContent>
          <AddToBagText>
            {needsSize ? 'Select a size' : 'Add to bag'}
          </AddToBagText>
          <AddToBagUnderline
            src={AddToBagUnderlineIcon}
            alt=""
            loading="lazy"
          />
        </AddToBagContent>
        {showDisclaimer && (
          <MobileDisclaimer>
            <Disclaimer />
          </MobileDisclaimer>
        )}
      </AddToBagButton>
      {showDisclaimer && (
        <DesktopDisclaimer>
          <Disclaimer />
        </DesktopDisclaimer>
      )}
    </SelectionControlsContainer>
  );
};

const QuantityDisclaimer = styled.div<{ $align?: string }>`
  text-align: ${({ $align }) => $align || 'center'};
  color: ${({ theme }) => theme.colors.error} !important;
  margin-top: 0.5rem;
`;

const MobileDisclaimer = styled.div`
  display: none;

  @media ${({ theme }) => theme.breakpoints.sm} {
    display: block;
  }
`;

const DesktopDisclaimer = styled.div`
  display: block;

  @media ${({ theme }) => theme.breakpoints.sm} {
    display: none;
  }
`;

const Section = styled(Box)`
  margin-bottom: 2.5rem;
  width: 100%;

  @media ${({ theme }) => theme.breakpoints.sm} {
    /* Clears the fixed Add to bag bar (ContentWrapper's 8rem) plus the size prompt below. */
    scroll-margin-bottom: calc(9.5rem + env(safe-area-inset-bottom, 0px));
  }

  &.flash-sale-color {
    margin-bottom: 1.5rem;
  }

  @media ${({ theme }) => theme.breakpoints.sm} {
    margin-bottom: 1rem;

    &.flash-sale-color {
      margin-bottom: 1rem;
    }
  }
`;

const ColorLabel = styled.p`
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.CircularStd};
  font-size: 1rem;
  line-height: normal;
  letter-spacing: 0.08px;
  text-transform: uppercase;
  margin: 0 0 0.5rem 0;
`;

const SizeLabel = styled.p`
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.CircularStd};
  font-size: 1rem;
  line-height: normal;
  letter-spacing: 0.08px;
  text-transform: uppercase;
  margin: 0;
`;

const SizeGuideButton = styled.button`
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.PantonRustHeavy};
  font-size: 1rem;
  font-weight: 100;
  line-height: normal;
  letter-spacing: 0.08px;
  text-transform: uppercase;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  &:hover {
    opacity: 0.7;
  }
`;

const SelectionControlsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;

  h5 {
    font-weight: 300;
    color: ${({ theme }) => theme.colors.cyanDark};
    margin-bottom: 0.625rem;
    font-size: 0.75rem;
    line-height: 1rem;
  }

  @media ${({ theme }) => theme.breakpoints.sm} {
    align-items: flex-start;

    h6 {
      font-size: 0.75rem;
    }
  }
`;

const SizeSectionContent = styled.div`
  width: fit-content;

  @media ${({ theme }) => theme.breakpoints.sm} {
    width: 100%;
  }
`;

const SizeLabelsWrapper = styled(Flex)`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const AddToBagButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: flex-start;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:hover:not(:disabled) > div {
    opacity: 0.7;
  }

  @media ${({ theme }) => theme.breakpoints.sm} {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: ${({ theme }) => theme.colors.colorMain};
    padding: 1rem 1rem calc(1.5rem + env(safe-area-inset-bottom, 0px)) 1rem;
    z-index: ${({ theme }) => theme.zIndex.high};
    transform: translateZ(0);
    -webkit-transform: translateZ(0);

    &:disabled {
      opacity: 1;
    }

    &:disabled span,
    &:disabled img {
      filter: grayscale(100%) brightness(0.5);
    }
  }
`;

const AddToBagContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: fit-content;
  transition: opacity 0.2s ease;
`;

const AddToBagText = styled.span`
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.PantonRustHeavy};
  font-size: 2.5rem;
  font-weight: 100;
  line-height: normal;
  letter-spacing: 0.2px;
  text-transform: uppercase;
`;

const AddToBagUnderline = styled(Image)`
  width: 100%;
  height: 0.25rem;
`;
